/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { addToCartTracking } from "@MasterBluePrint/tracking";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks} from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    indexCarouselPosition?: number;
    pageSource?: string;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    return {
        productSettings: getSettingsCollection(state).productSettings,
        pagination: getProductListDataViewProperty(state, "pagination"),
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        addingProductToCart: state.context.isUpdatingCart,
        state,
        pageType: getCurrentPage(state).type,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable(addToCart),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired },
        productInfo: { qtyOrdered, unitOfMeasure, pricing },
        idx
    },
    productSettings,
    hasEnoughInventory,
    addingProductToCart,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    state,
    pageType,
    indexCarouselPosition,
    pageSource,
    pagination,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const [addingToCartLoading, setaddingToCartLoading] = useState(false);
    if (!productSettings.canAddToCart || !hasEnoughInventory || !canAddToCart) {
        return null;
    }

    if (pricing && getUnitNetPrice(pricing, 1).price === 0) {
        return null;
    }
        
    const addToCartClickHandler = async () => {
        setaddingToCartLoading(true);
        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                    .filter(sectionOptionId => !!sectionOptionId)
                    .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const indexPosition = Number(idx);
        idx = !Number.isNaN(indexPosition) ? idx : indexCarouselPosition;
        let productListPosition:any;
        if(idx || idx===0)
        {
            if(pagination && pagination?.page>1)
            {
                productListPosition=(pagination.pageSize *(pagination.page-1))+(idx+1);
            }
            else
            {
                productListPosition=idx+1;
            }
        }
        const currentUrl = window.document.URL;     
        const pageSourceResult = currentUrl.toLowerCase().includes("search") ? 'Search Results' :
            currentUrl.toLowerCase().includes("checkout/review") ? 'Order Review' : currentUrl.toLowerCase().includes("brand") ? 'Brand Results'
                : currentUrl.toLowerCase().includes("quote/confirmation") ? 'Quote Confirmation' : currentUrl.toLowerCase().includes("mylists") ? "Product Wish List" : currentUrl.toLowerCase().includes("category") ? 'Category Results': currentUrl.toLowerCase().includes("staticlist") ? "Shared Cart" : pageSource ?? "Product Details";

        let listId;
        if (pageSourceResult === "Shared Cart") {
            const wishListState = getWishListState(state, state.pages.staticList.wishListId);
            const wishList = wishListState.value;
            listId= wishList?.id;
        }
        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            additionalQueryStringParameters: { idx, pageSourceResult,listId,productListPosition },            
            onError: (error: any) => {
                toasterContext.addToast({
                    body: translate(error),
                    messageType: "danger",
                });
            },
        })) as CartLineModel;  
        if (cartline && cartline.id) {
            addToCartTracking(cartline, state);
        }
        if (!addingProductToCart) {
            setaddingToCartLoading(false);
        }
        if (productSettings.showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
            });
        }
    };

    return (
        <Button
            style={{
                color: "#002554",
                fontFamily: "'Roboto', sans-serif",
                fontSize: "1rem",
                padding: "0",
                fontWeight: "bold",
                textTransform: "uppercase",
                marginBottom: "1.2rem",
                marginTop: ".5rem",

            }}
            {...styles}
            onClick={addToCartClickHandler}
            disabled={qtyOrdered <= 0 || addingProductToCart}
            {...otherProps}
            data-test-selector={`${productId}`}
        >
            {addingProductToCart && addingToCartLoading ?
                <>
                    {translate("ADDING TO CART")}
                    <LoadingSpinner
                        style={{
                            width: "0.8rem",
                            height: "0.8rem",
                            marginLeft: "0.6rem",
                            marginTop: "0.2rem",
                        }}
                    />
                </> :
                `${labelOverride ?? translate("ADD TO CART")}`}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
